import React from 'react';
import PropTypes from 'prop-types';
import { FaBook, FaGlobeEurope, FaMusic } from 'react-icons/fa';
import { useTranslation } from '../../utils/translations';
import './ActivityGroupedView.css';

const iconForActivity = (id) => {
  switch (id) {
    case 'sundaySchool':
      return <FaBook aria-hidden="true" />;
    case 'russianSchool':
      return <FaGlobeEurope aria-hidden="true" />;
    case 'childrensChoir':
      return <FaMusic aria-hidden="true" />;
    default:
      return null;
  }
};

// Compact formatter that shows all teachers/leaders with proper wrapping
const joinWithLimit = (arr = [], limit = 2) => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  // Wrap each name in a span to prevent breaking individual names
  const wrappedNames = arr.map(name => `<span style="white-space: nowrap;">${name}</span>`);
  return wrappedNames.join(', ');
};

// Format birthdate for display
const formatBirthdate = (birthdate) => {
  if (!birthdate) return '';
  try {
    const [year, month, day] = birthdate.split('-').map(Number);
    const date = new Date(year, month - 1, day); // local date, not UTC
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch (error) {
    return birthdate; // Return original if parsing fails
  }
};

const ActivityGroupedView = ({ activities, students }) => {
  const { t } = useTranslation();
  
  // index activities by id for fast access
  const activityMap = activities.reduce((acc, a) => {
    acc[a.id] = a;
    return acc;
  }, {});

  // Helper to get a student's activity details by id
  const getStudentActivity = (student, activityId) =>
    student.activities.find((sa) => sa.id === activityId);

  return (
    <div className="activity-grouped-layout" role="main" aria-label="Activities grouped view">
      {['sundaySchool', 'russianSchool', 'childrensChoir'].map((aid) => {
        const activity = activityMap[aid];
        const studentsWithActivity = students.filter(student => {
          const activity = getStudentActivity(student, aid);
          // Check if activity exists and has meaningful details
          return activity?.details && 
                 typeof activity.details === 'object' && 
                 Object.keys(activity.details).length > 0;
        });

        // Don't render activity section if no students are enrolled
        if (studentsWithActivity.length === 0) {
          return null;
        }

        return (
          <div key={aid} className="activity-container" data-activity={aid}>
            <div className="activity-container-header">
              <span className="activity-icon">{iconForActivity(aid)}</span>
              <h3 className="activity-title">{t(`activities.${aid}`) || activity?.name || aid}</h3>
            </div>
            <div className="students-in-activity">
              {studentsWithActivity.map((student) => {
                const sa = getStudentActivity(student, aid);
                const d = sa?.details || {};
                const room = d.classroom || d.choirRoom;
                const main = d.class || d.choirNumber;
                const people = d.teachers || d.choirLeaders;
                const peopleText = joinWithLimit(people, 2);
                const labels = aid === 'childrensChoir'
                  ? { main: t('details.choirNumber'), people: t('details.choirLeaders') }
                  : { main: t('details.class'), people: t('details.teachers') };

                return (
                  <div key={student.id} className="student-activity-card">
                    <div className="student-info">
                      <h4 className="student-name">{student.name}</h4>
                      <span className="day-badge">{d.day}</span>
                    </div>
                    <div className="activity-details">
                      <div className="detail-row">
                        <span className="detail-label">{labels.main}:</span>
                        <span className="detail-value">{main}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">{t('details.room')}:</span>
                        <span className="detail-value">{room}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">{labels.people}:</span>
                        <span 
                          className="detail-value" 
                          dangerouslySetInnerHTML={{ __html: peopleText }}
                        ></span>
                      </div>
                      {student.birthdate && (
                        <div className="detail-row">
                          <span className="detail-label">{t('details.birthday')}:</span>
                          <span className="detail-value">{formatBirthdate(student.birthdate)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

ActivityGroupedView.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      activities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          status: PropTypes.string,
          details: PropTypes.object,
        })
      ).isRequired,
    })
  ).isRequired,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      day: PropTypes.string,
    })
  ).isRequired,
};

export default ActivityGroupedView;
